/**
 * Vertical scale for the portfolio sparklines.
 *
 * A pure min/max fit makes every series fill the box, which means the SHAPE is
 * always dramatic regardless of how much actually moved. A NinjaTrader demo
 * account that went 49,940.90 → 49,952.90 → 49,911.40 — a $41.50 range, 0.083%
 * of its value — rendered as a peak and a cliff, sitting directly above a
 * Coinbase row that genuinely lost 99.94%. Both looked equally catastrophic.
 *
 * So the drawn range gets a floor proportional to the account's magnitude:
 * below it, a move renders as the near-flat line it is. Above it, nothing
 * changes and real moves still fill the box.
 */

/**
 * Minimum drawn range, as a fraction of the series' magnitude.
 *
 * 2% is the point where a move starts being worth looking at on a trading
 * account. A 0.08% wiggle then occupies ~4% of the height — visible on close
 * inspection, unmistakably flat at a glance, which is the honest reading.
 */
export const MIN_RANGE_RATIO = 0.02;

/**
 * @param {number[]} values
 * @returns {{min: number, max: number, range: number, floored: boolean}}
 *   Bounds to draw between. `floored` reports whether the range was widened,
 *   so a caller can label or style the flat case if it wants to.
 */
export function sparklineBounds(values) {
  const nums = (values || []).filter((v) => Number.isFinite(Number(v))).map(Number);
  if (!nums.length) return { min: 0, max: 1, range: 1, floored: false };

  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const actualRange = max - min;

  // Magnitude, not the range itself: a $41 move means something very different
  // on a $50 account than on a $50,000 one, and only the account's own size
  // says which this is.
  const magnitude = Math.max(Math.abs(max), Math.abs(min));
  const floor = magnitude * MIN_RANGE_RATIO;

  // A dead-flat series has no magnitude to scale from when it sits at zero;
  // fall back to 1 so the divide stays finite and the line renders centered.
  if (actualRange >= floor || floor <= 0) {
    return {
      min, max, range: actualRange || 1, floored: false,
    };
  }

  // Widen symmetrically so the line sits in the middle of the box rather than
  // pinned to an edge, which would imply a trend that is not there.
  const mid = (max + min) / 2;
  return {
    min: mid - floor / 2,
    max: mid + floor / 2,
    range: floor,
    floored: true,
  };
}
